import React, { useMemo } from "react";

import { UrlProvider } from "../../../../../api/UrlProvider";
import withTranslations from "../../../../../hocs/withTranslations";
import useStrategyDashboardChartMarketFromUrl from "../../../../../hooks/useStrategyDashboardChartMarketFromUrl";
import {
  getStrategyDashboardI18nKey,
  shouldShowStrategyDashboardSignals
} from "../../../../../lib/StrategyDashboard/strategyDashboardConfig";
import { getStrategyDashboardChartMarkets } from "../../../../../lib/StrategyDashboard/strategyDashboardChartMarkets";
import { useStrategyDashboardConfig } from "../../../../../lib/StrategyDashboard/StrategyDashboardContext";
import {
  BodyRegular,
  ButtonWithArrow,
  H3,
  H5
} from "../../../common/Typography";
import StrategyBenchmarkChart from "../../../common/StrategyBenchmarkChart";
import StrategyEbookCard from "./StrategyEbookCard";
import StrategyIndicatorsTable from "./StrategyIndicatorsTable";
import StrategyLatestNotifications from "./StrategyLatestNotifications";
import StrategySignalsTable from "./StrategySignalsTable";

const StrategyDashboardOverview = ({ translate, locale }) => {
  const dashboard = useStrategyDashboardConfig();
  const i18nKey = suffix => getStrategyDashboardI18nKey(dashboard, suffix);
  const chartMarkets = useMemo(
    () => getStrategyDashboardChartMarkets(dashboard),
    [dashboard]
  );
  const { selectedMarket } =
    useStrategyDashboardChartMarketFromUrl(chartMarkets);
  const descriptionUrl = UrlProvider.getLocalizedUrl(
    locale,
    `fe.${dashboard.web.urlPatternKeys.description.component}`,
    { locale }
  );

  return (
    <div className="bg-white py-6 -mx-3.6 md:bg-transparent md:mx-0 md:py-8 md:px-0">
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 md:items-start mb-6 md:mb-8">
        <div className="flex flex-col align-start h-full px-4 md:px-0">
          <H3 tag="h1">{translate(i18nKey("heading"))}</H3>
          <BodyRegular className="mt-4 md:mt-6 mb-4 md:mb-6 text-black-5 text-justify">
            {translate(i18nKey("description"))}
          </BodyRegular>
          <ButtonWithArrow
            href={descriptionUrl}
            className="w-full min-w-0 md:min-w-332 md:w-auto"
          >
            {translate(i18nKey("show_full_description"))}
          </ButtonWithArrow>
          {dashboard.ebook ? (
            <div className="hidden md:block mt-auto pt-6">
              <StrategyEbookCard />
            </div>
          ) : null}
        </div>
        <hr className="hr-horizontal-3 md:hidden" />
        {dashboard.chartStrategyId ? (
          <div>
            <StrategyBenchmarkChart
              strategy={dashboard.chartStrategyId}
              markets={chartMarkets || undefined}
              selectedMarket={selectedMarket || undefined}
            />
          </div>
        ) : null}
      </div>
      {shouldShowStrategyDashboardSignals(dashboard) ? (
        <>
          <hr className="hr-horizontal-3 md:hidden mt-6" />
          <div className="flex flex-col mt-8 px-4 md:rounded md:bg-white md:py-6 md:px-10">
            <H5 tag="h2" className="mb-3">
              {translate(i18nKey("signals_table_heading"))}
            </H5>
            <BodyRegular className="mb-6 text-black-5">
              {translate(i18nKey("signals_table_description"))}
            </BodyRegular>
            <StrategySignalsTable preview />
          </div>
        </>
      ) : null}
      {dashboard.notificationTypes ? (
        <>
          <hr className="hr-horizontal-3 md:hidden mt-6" />
          <StrategyLatestNotifications />
        </>
      ) : null}
      {dashboard.indicatorsTable ? (
        <>
          <hr className="hr-horizontal-3 md:hidden mt-6" />
          <div className="flex flex-col mt-8 px-4 md:rounded md:bg-white md:py-6 md:px-10">
            <H5 tag="h2" className="mb-3">
              {translate(i18nKey("indicators_table_heading"))}
            </H5>
            <BodyRegular className="mb-6 text-black-5">
              {translate(i18nKey("indicators_table_description"))}
            </BodyRegular>
            <StrategyIndicatorsTable preview />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default withTranslations(StrategyDashboardOverview);
